// REAL-DATA availability + counts for DYNAMIC (inline-option) filter fields — the class the filter-ai
// catalog cannot count.
//
// Dynamic select/boolean fields (recommendation_dynamic_field__* / building_dynamic_field__*) carry
// their option vocabulary INLINE from configuration (field_options), not from the /filter_catalog fact
// views — so resolution grounds against CONFIGURED values that may have zero actual records in this
// project, and /filter_catalog reports them in `uncounted_dimensions` (the "N Records" preview then
// hides rather than misleads). The dashboard itself has always had the truth: the FilterModal
// lazy-loads each dynamic dropdown WITH per-option counts from the Ruby /api/v1/master_filters/get_list,
// faceted by the current project scope + every OTHER applied filter. These helpers mirror that exact
// call (the params useMyFilters.handleFilterView builds on the dashboard branch) so Otto can:
//   (a) hide/warn about zero-result dynamic options like any catalog-covered field (cascade guard), and
//   (b) recover the record total for the Apply-row preview instead of hiding it (feasibility).
//
// Best-effort by contract: any failure returns null and callers degrade to the old "unverifiable"
// behavior — infrastructure must never block the user or show a number we can't stand behind.

import { buildDashboardFilterParams } from "./applyMapping";

const glfKeyOf = meta => (meta && Array.isArray(meta.get_list_field) ? meta.get_list_field.join("|") : "");

// The dashboard paramKey the field applies under. Strictly meta.paramKey (threaded through
// augmentMapsWithFilterFields), with ONE narrow fallback: an inline-option dynamic field's getListKey
// IS [paramKey] (filterFieldBuilders.dynamicGetListKey), so maps built before paramKey was threaded
// still resolve. Never fall back for catalog dims — their get_list_field ("trades.id") is NOT a
// paramKey, and a wrong key here would condition the count on a filter the dashboard doesn't have.
const paramKeyOf = meta => {
    if (!meta) return null;
    if (meta.paramKey) return meta.paramKey;
    if (
        Array.isArray(meta.get_list_field) &&
        meta.get_list_field.length === 1 &&
        meta.inlineOptions &&
        meta.inlineOptions.length
    ) {
        return meta.get_list_field[0];
    }
    return null;
};

/**
 * A field whose availability/counts Otto can fetch from the dashboard's own /get_list: the dynamic
 * select/boolean fields, whose options are config-sourced (inlineOptions) and therefore invisible to
 * the /filter_catalog fact views. This is the class whose record TOTAL we trust enough to display
 * (feasibility recovery). FCI/EFCI "Description" bands are also catalog-uncounted, but their /get_list
 * total semantics (filter-aware FCI computed at request time) are not validated from here yet, so
 * their totals deliberately stay unverifiable (they ARE re-countable for pill greying — below).
 */
export const isGetListCountable = meta =>
    !!(
        meta &&
        meta.isRecommendationField &&
        !meta.isRecommendationTable &&
        meta.searchKey &&
        meta.inlineOptions &&
        meta.inlineOptions.length
    );

/**
 * The broader class: any field whose per-option counts the dashboard's own /get_list can serve — the
 * FilterModal's generic dropdown path (isRecommendationField + searchKey + either inline options or a
 * real getListKey WITH a known paramKey, so the field's own dimension can be excluded from the
 * conditioning params). Used to RE-COUNT the fields offered on a card when the cascade holds an arm
 * /filter_catalog can't count (a dynamic pick, an FCI band) — the catalog's counts ignore that arm and
 * over-count, so a pill the uncountable pick empties would never grey without this.
 */
export const isGetListRecountable = meta =>
    !!(
        meta &&
        meta.isRecommendationField &&
        !meta.isRecommendationTable &&
        meta.searchKey &&
        ((meta.inlineOptions && meta.inlineOptions.length) ||
            (Array.isArray(meta.get_list_field) && meta.get_list_field.length && meta.paramKey))
    );

// The cascade / uncounted_dimensions are keyed by get_list_field; dimMap is keyed by field id.
export const metaByGlf = (maps, glf) => {
    const dimMap = (maps && maps.dimMap) || {};
    const ids = Object.keys(dimMap);
    for (let i = 0; i < ids.length; i++) {
        if (glfKeyOf(dimMap[ids[i]]) === glf) return dimMap[ids[i]];
    }
    return null;
};

// A RE-COUNTABLE meta for this get_list_field, wherever it lives in the dimMap. The /schema id and the
// dashboard field key don't always coincide (schema `trade` vs dashboard `trades`): resolution rides
// the schema meta (no paramKey/searchKey — not re-countable), while its dashboard TWIN — same
// get_list_field, added by augmentMapsWithFilterFields — carries the routing flags. Counts are keyed
// by glf, so fetching via the twin serves the schema-keyed suggestion identically.
export const recountableMetaByGlf = (maps, glf) => {
    if (!glf) return null;
    const dimMap = (maps && maps.dimMap) || {};
    const ids = Object.keys(dimMap);
    for (let i = 0; i < ids.length; i++) {
        const meta = dimMap[ids[i]];
        if (glfKeyOf(meta) === glf && isGetListRecountable(meta)) return meta;
    }
    return null;
};

// Ensure the /get_list call is project-scoped even when the dashboard params don't carry project_ids
// (mirrors withDashboardProjectIds on the modal path — the count would be portfolio-wide without it).
export const withProjectScope = (raw, scope) => {
    const base = { ...(raw || {}) };
    if (!(Array.isArray(base.project_ids) && base.project_ids.length)) {
        const ids =
            scope && Array.isArray(scope.projectIds) && scope.projectIds.length
                ? scope.projectIds
                : scope && scope.projectId != null
                ? [scope.projectId]
                : null;
        if (ids) base.project_ids = ids;
    }
    return base;
};

/**
 * Mirror useMyFilters.handleFilterView's dashboard-branch request for ONE field:
 *   { searchKey, field, ...appliedFilters, is_dynamic? }
 * `field` is the inline option ids for dynamic selects (booleans: the real Yes/No pair) or the field's
 * getListKey for catalog dims; `is_dynamic` rides only for dynamic fields — both exactly as the modal
 * sends them. appliedFilters = `conditionParams` minus this field's own paramKey and its
 * exclude_filters entry — values within a dimension are OR'd on the dashboard, so a field is always
 * counted under the OTHER filters only (same-dimension exclusion is inherent to the modal call).
 */
export const buildDynamicListParams = (meta, conditionParams) => {
    const paramKey = paramKeyOf(meta);
    const { [paramKey]: omit, ...rest } = conditionParams || {};
    if (rest.exclude_filters) {
        const { [paramKey]: omitExclude, ...restExclude } = rest.exclude_filters;
        rest.exclude_filters = restExclude;
    }
    const hasInline = meta.inlineOptions && meta.inlineOptions.length;
    return {
        searchKey: meta.searchKey,
        field: meta.isBoolean ? ["Yes", "No"] : hasInline ? meta.inlineOptions.map(o => o.id) : meta.get_list_field,
        ...rest,
        ...(meta.isDynamicField ? { is_dynamic: true } : {})
    };
};

/**
 * Model the RAW dashboard params (`dashboardFilterParams`) the dashboard would hold AFTER applying the
 * card's current selections — the conditioning state for a post-apply /get_list count. Returns null when
 * the state can't be modeled truthfully (then callers keep today's "unverifiable" behavior):
 *   - `raw` missing while filters ARE applied (`baseApplied`) — we can't reproduce the applied state;
 *   - a card-touched dimension whose paramKey is unknown — we can't write its selection into the params.
 * `cascade` is the ALREADY-MERGED post-apply dimension state (mergeSelectionCascade output), so set/add/
 * remove semantics are inherited from it; `touchedGlfs` are the dimensions the card actually modifies.
 * `wildcards` are the card's SELECTED wildcard pills (cost thresholds etc.) in the neutral apply shape —
 * translated through buildDashboardFilterParams, the SAME chokepoint the real Apply uses (operator map,
 * cost-field aliasing, magnitude expansion), so the modeled conditioning can't drift from what Apply
 * would send. That translation needs the dashboard `filterFields`; a selected wildcard we can't express
 * truthfully makes the whole state unmodelable (null).
 */
export const modelPostApplyRawParams = ({ raw, baseApplied, cascade, touchedGlfs, intent, maps, scope, wildcards, filterFields }) => {
    if (!raw && baseApplied) return null;
    let base = { ...(raw || {}) };
    if (intent && intent.clear_all) {
        const kept = {};
        if (base.project_ids) kept.project_ids = base.project_ids;
        if (base.client_ids) kept.client_ids = base.client_ids;
        base = kept;
    }
    ((intent && intent.clears) || []).forEach(id => {
        const meta = maps && maps.dimMap && maps.dimMap[id];
        const paramKey = meta && meta.paramKey;
        if (paramKey) delete base[paramKey];
    });
    const touched = touchedGlfs || [];
    for (let i = 0; i < touched.length; i++) {
        const glf = touched[i];
        const meta = metaByGlf(maps, glf);
        const paramKey = paramKeyOf(meta);
        if (!paramKey) return null; // a selection we can't express -> the modeled state would lie
        const values = cascade && cascade.dimensions && cascade.dimensions[glf];
        if (values && values.length) base[paramKey] = values;
        else delete base[paramKey]; // dimension emptied by removes
    }
    if (cascade && cascade.year) {
        base.start_year = cascade.year.start;
        base.end_year = cascade.year.end;
    }
    const wc = wildcards || [];
    if (wc.length) {
        if (!filterFields || !filterFields.length) return null;
        // The apply payload keys wildcards by `field_id` (buildApplyPayload emits {field_id: w.field});
        // the card's pills carry `field` — translate the same way so applyMapping resolves the config.
        const payload = {
            wildcards: wc.map(w => ({
                field_id: w.field_id || w.field,
                operator: w.operator,
                value: w.value,
                value_range: w.value_range
            }))
        };
        const { params, diagnostics } = buildDashboardFilterParams({ payload, filterFields, current: base });
        if (diagnostics && diagnostics.wildcardsDropped && diagnostics.wildcardsDropped.length) return null;
        base = params;
    }
    return withProjectScope(base, scope);
};

// Single-flight de-dup: the guard and the card's debounced feasibility re-checks can ask for the same
// field under the same conditioning state in quick succession — identical in-flight requests coalesce.
// Deliberately NOT a cache (same contract as fetchCascadedCounts): filter state changes turn-to-turn,
// and a stale count right after an apply is exactly the bug this module exists to prevent.
const pending = new Map();

/**
 * Fetch the REAL per-option counts for one dynamic field from the dashboard's own /get_list.
 * Returns { list: [{id,name,count}], byKey: {String(id ?? name) -> count} } or null (unverifiable):
 * network/HTTP failure, success:false, or a list that carries no counts at all (an endpoint variant we
 * don't understand must degrade, never read as "everything is zero" and hide valid options).
 */
export const fetchDynamicOptionCounts = async ({ meta, conditionParams }) => {
    if (!isGetListRecountable(meta)) return null;
    const params = buildDynamicListParams(meta, conditionParams);
    const key = `${glfKeyOf(meta)}|${JSON.stringify(params)}`;
    if (pending.has(key)) return pending.get(key);

    const flight = (async () => {
        try {
            // Lazy require: keep the dashboard services (axios gateway) out of this module's import graph
            // so isolated renders/tests of the pure helpers never pull the HTTP layer (same pattern as
            // core/telemetry's lazy service require).
            const { getRecommendationMasterFilter } = require("../../../dashboard/services");
            const res = await getRecommendationMasterFilter(params);
            if (!res || res.status !== 200 || !res.data || res.data.success === false) return null;
            const rawList = Array.isArray(res.data.list) ? res.data.list : null;
            if (!rawList) return null;
            // TRUNCATED LIST (FCA-7464): /get_list may now answer with a CAPPED `list` alongside a `count`
            // holding the dimension's real option total — the filter window renders "Showing N out of M" and
            // re-queries with `search_filters` as the user types. Otto has no search term to narrow with, so a
            // capped list is not a smaller answer, it is a WRONG one: every option past the cap is absent from
            // `byKey`, which reads downstream as "this option has no rows" — greying a valid pill and making a
            // real value look unavailable. Degrade to unverifiable, the same verdict this function already
            // gives any endpoint variant it can't trust, so callers skip the check instead of believing a zero.
            const totalOptions = res.data.count;
            if (typeof totalOptions === "number" && totalOptions > rawList.length) return null;
            if (rawList.length && !rawList.some(o => typeof o.count === "number")) return null;
            const list = rawList.map(o => ({
                id: o.id !== undefined && o.id !== null ? o.id : null,
                name: o.name != null ? o.name : o.id,
                count: typeof o.count === "number" ? o.count : 0
            }));
            const byKey = {};
            list.forEach(o => {
                byKey[String(o.id !== null && o.id !== undefined ? o.id : o.name)] = o.count;
            });
            return { list, byKey };
        } catch (e) {
            return null;
        }
    })();
    pending.set(key, flight);
    try {
        return await flight;
    } finally {
        pending.delete(key);
    }
};

/**
 * Guard-time helper (orchestrator.buildProposal): the distinct dynamic fields among a resolution's
 * suggestions — the fields whose configured options must be validated against real data before Otto
 * offers them. Exported for unit tests.
 */
export const collectDynamicSuggestionMetas = (results, maps) => {
    const seen = new Set();
    const metas = [];
    (results || []).forEach(r => {
        const candidates = [...(r.suggestions || []), ...(r.chosen ? [r.chosen] : [])];
        candidates.forEach(s => {
            if (!s || s.value_kind === "text" || s.field === "search") return;
            const meta = maps && maps.dimMap && maps.dimMap[s.field];
            if (meta && isGetListCountable(meta) && !seen.has(meta.id)) {
                seen.add(meta.id);
                metas.push(meta);
            }
        });
    });
    return metas;
};
